import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StopOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { DatePicker, Select, Button, Form, Input } from 'antd';
import { useShallow } from 'zustand/shallow';
import AlertBox from '@/components/alert-box';
import { Dialog } from '@/components/dialog/Dialog';
import EquipmentToReturn from '@/components/equipment-requests/EquipmentToReturn';
import UploadFiles from '@/components/upload/UploadFiles';
import { useNotify, useQueryParams } from '@/hooks';
import { cn } from '@/libs/utils';
import { API } from '@/services/api.service';
import { listDocumentTemplates } from '@/services/documentTemplates.service';
import { QUERY_KEY } from '@/shared/constants';
import { useSalaryStore } from '@/store/useSalaryStore';
import { DATE_FORMATS, formatDateForBackend } from '@/utils/formUtils';
import { getUserFullName } from '@/utils/utils';
import { ToggleHeader, ToggleSwitch } from '../toggle-switch';
import TerminationNotifyDialog from './TerminationNotifyDialog';
import {
  DOCUMENT_BUILTIN,
  DOCUMENT_NONE,
  TERMINATION_REASON_KEYS,
  TERMINATION_REASON_NOTE_MAX_LENGTH,
  defaultDocumentValue,
  parseDocumentValue,
  templateDocumentValue,
  terminationDocumentTemplates,
} from './terminationShared';

export default function TerminateActionModal({
  metadata,
  disabled = false,
  employeeId,
  // A page without ?company= (the company profile) passes it here.
  companyId,
  // Keeps the component (and its "Notify" step) mounted without the button,
  // where the button must go away once the termination is saved.
  hideTrigger = false,
  // Where the row unmounts right after saving, the "Notify" step would be
  // torn down with it; there HR notifies from the employee profile.
  offerNotifyAfterSave = true,
}) {
  const { t } = useTranslation([
    'forms',
    'tables',
    'menus',
    'messages',
    'translations',
  ]);
  const tt = (key, opts) =>
    t(`terminationUi.${key}`, { ns: 'translations', ...opts });
  const notification = useNotify();
  const queryClient = useQueryClient();
  const {
    queryParams: { company },
  } = useQueryParams();

  const [form] = Form.useForm();

  const companyIdFromQueryParams = useMemo(() => {
    if (companyId) return companyId;
    return company && company !== 'all' ? company : undefined;
  }, [company, companyId]);

  const initialValues = {
    terminationDocument: false,
    company: companyIdFromQueryParams,
    employee: employeeId,
  };

  useEffect(() => {
    form.setFieldsValue({
      terminationDocument: false,
      company: companyIdFromQueryParams,
      employee: employeeId,
    });
  }, [form, employeeId, companyIdFromQueryParams]);

  const [toggle, setToggle] = useState(false);
  // Employee whose termination was just saved: the "Notify" step opens for it.
  const [notifyEmployeeId, setNotifyEmployeeId] = useState(null);

  const { isDirty } = useSalaryStore(
    useShallow((state) => ({
      isDirty: state.isDirty,
    }))
  );

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleUploadedFiles = useCallback(({ action: { type, payload } }) => {
    switch (type) {
      case 'upload':
        setUploadedFiles((prev) => {
          const exists = prev.some((f) => f.uid === payload.uid);
          return exists ? prev : [...prev, payload];
        });
        break;
      case 'remove':
        setUploadedFiles((prev) => prev.filter((file) => file.uid !== payload));
        break;
      case 'reset': {
        setUploadedFiles([]);
        break;
      }
      default:
        break;
    }
  }, []);

  const { data: companyOptions, isLoading: companyIsLoading } = useQuery({
    queryKey: [QUERY_KEY.companiesDropdown],
    queryFn: async () => {
      return await API.companies.retrieveDropdownAllCompanies(null);
    },
    select: (data) => {
      const companies = data?.items || [];

      return companies?.map((company) => ({
        label: company.name,
        value: company._id,
      }));
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  const isSelectedCompany = Form.useWatch('company', form);

  useEffect(() => {
    if (!employeeId) {
      form.setFieldsValue({ employee: undefined });
    }
  }, [form, isSelectedCompany, employeeId]);

  const { data: employeeOptionsData, isLoading: employeeOptionsIsLoading } =
    useQuery({
      queryKey: [QUERY_KEY.employeesDropdown, isSelectedCompany],
      queryFn: async () =>
        await API.employees.retrieveEmployeeEmploymentEmployees({
          companyId: isSelectedCompany,
          type: 'termination',
        }),
      select: (data) => {
        return data?.items;
      },
      enabled: !!isSelectedCompany,
    });

  // The company's documents HR may pick for the termination — the same list
  // the custom-document form offers (same query, one request).
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['documentTemplates', isSelectedCompany, 'employee'],
    queryFn: () => listDocumentTemplates(isSelectedCompany, false, 'employee'),
    enabled: Boolean(toggle && isSelectedCompany),
    retry: false,
  });
  const documentTemplates = useMemo(
    () => terminationDocumentTemplates(templatesData || []),
    [templatesData]
  );
  const documentOptions = [
    ...(documentTemplates.length
      ? [
          {
            label: tt('companyDocuments'),
            options: documentTemplates.map((tpl) => ({
              value: templateDocumentValue(tpl._id),
              label: tpl.name,
            })),
          },
        ]
      : []),
    {
      label: tt('otherOptions'),
      options: [
        { value: DOCUMENT_BUILTIN, label: tt('builtinDocument') },
        { value: DOCUMENT_NONE, label: tt('noDocumentOption') },
      ],
    },
  ];

  // Today's default once the company's templates are known, unless HR
  // already picked something.
  useEffect(() => {
    if (!toggle || !templatesData) return;
    if (form.isFieldTouched('terminationDocumentValue')) return;
    form.setFieldsValue({
      terminationDocumentValue: defaultDocumentValue(templatesData),
    });
  }, [form, toggle, templatesData]);

  const reasonValue = Form.useWatch('terminationReason', form);
  const selectedEmployee = Form.useWatch('employee', form);
  const attachExternal = Form.useWatch('terminationDocument', form);

  const reasonOptions = TERMINATION_REASON_KEYS.map((key) => ({
    value: key,
    label: tt(`reasons.${key}`),
  }));

  const employeeOptions = employeeOptionsData?.length
    ? employeeOptionsData?.map((company) => ({
        value: company._id,
        label: getUserFullName(company.firstName, company.lastName),
      }))
    : [];

  const handleCancelModal = () => {
    setToggle(false);
  };

  const onReset = () => {
    form.resetFields();
    handleCancelModal();
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (formValues) => {
      const {
        terminationDocumentValue,
        terminationDocument: attachExternalDocument,
        ...rest
      } = formValues;
      const payload = {
        ...rest,
        ...parseDocumentValue(terminationDocumentValue),
        type: 'termination',
      };
      if (payload.terminationReason !== 'OTHER') {
        delete payload.terminationReasonNote;
      }

      payload.endDate = formatDateForBackend(
        payload.endDate,
        DATE_FORMATS.STANDARD
      );
      payload.dateOfCreation = formatDateForBackend(
        payload.dateOfRequestCreate,
        DATE_FORMATS.STANDARD
      );
      delete payload.dateOfRequestCreate;

      const formDataToSend = new FormData();
      for (const [key, value] of Object.entries(payload)) {
        formDataToSend.append(key, JSON.stringify(value));
      }

      // Only with "Attach an external document" on: the files go to the
      // employee's documents, linked to this termination.
      if (attachExternalDocument) {
        uploadedFiles.forEach((file) => {
          formDataToSend.append('files', file.originFileObj);
          formDataToSend.append('createTerminationDoc', true);
        });
      }

      const response =
        await API.employees.updateEmploymentStatus(formDataToSend);
      return { response, employee: formValues.employee };
    },
    onSuccess: ({ response, employee }) => {
      notification.success({
        message: response.message,
        description: offerNotifyAfterSave ? undefined : tt('savedNoEmailHint'),
      });
      queryClient.invalidateQueries([QUERY_KEY.employees]);
      onReset();
      handleUploadedFiles({ action: { type: 'reset' } });
      // Saving sent no email; offer the notice right away.
      if (offerNotifyAfterSave && response?.contractId && employee) {
        setNotifyEmployeeId(employee);
      }
    },
    onError: (error) => {
      console.error('Failed to submit change of employment:', error);
      notification.error({
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to submit change of employment',
      });
    },
  });

  const onFinish = async (values) => {
    await mutateAsync(values);
  };

  const dotButtonAnimation = (dotColor) => {
    return (
      <span
        className={cn(
          'block h-2 w-2 shrink-0 rounded-full',
          dotColor ? dotColor : 'bg-zinc-100'
        )}
      >
        <span
          className={cn(
            'block h-2 w-2 shrink-0 rounded-full group-hover:animate-ping',
            dotColor ? dotColor : 'bg-zinc-100'
          )}
        />
      </span>
    );
  };

  return (
    <>
      <Dialog
        form={form}
        onSubmit={onFinish}
        title={t('terminate', { ns: 'menus' })}
        open={toggle}
        className="!h-auto !w-[768px]"
        onCancel={handleCancelModal}
        confirmLoading={isPending}
        destroyOnClose
        destroyOnHidden
        triggerChildren={
          hideTrigger ? null : metadata?.showDot ? (
            <Button
              title={t('terminate', { ns: 'menus' })}
              type="danger"
              className={cn(
                'btn-default group relative justify-start px-0',
                metadata?.buttonClassName
              )}
              onClick={() => setToggle(true)}
              disabled={isDirty}
            >
              {dotButtonAnimation('bg-danger')}

              <span className="mr-2">{t('terminate', { ns: 'menus' })}</span>
            </Button>
          ) : (
            <Button
              title={t('terminate', { ns: 'menus' })}
              type="danger"
              className={cn('btn-danger h-8', metadata?.buttonClassName)}
              icon={<StopOutlined />}
              onClick={() => setToggle(true)}
              disabled={isDirty}
            >
              <span className="mr-2">{t('terminate', { ns: 'menus' })}</span>
            </Button>
          )
        }
      >
        <div className="-mx-4 -my-6 grid max-h-[calc(100svh_-_298px)] gap-4 overflow-y-auto px-4 py-6">
          <Form
            form={form}
            name="terminate-form"
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
            className={cn('form')}
            initialValues={initialValues}
          >
            <AlertBox variant="danger" className="relative p-2">
              <span>
                {t('confirmEmploymentTerminationMessage', { ns: 'messages' })}
              </span>
            </AlertBox>

            <div className="grid w-full">
              <Form.Item
                label={t('selectCompany', { ns: 'forms' })}
                name="company"
                rules={[
                  {
                    required: true,
                    message: t('formValidation.companyRequired', {
                      ns: 'forms',
                    }),
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  mode="single"
                  className="w-full max-w-[726px]"
                  placeholder={t('selectCompany', { ns: 'forms' })}
                  optionFilterProp="label"
                  loading={companyIsLoading}
                  options={companyOptions}
                  disabled={disabled}
                />
              </Form.Item>

              <Form.Item
                label={t('selectEmployee', { ns: 'forms' })}
                name="employee"
                rules={[
                  {
                    required: true,
                    message: t('formValidation.companyRequired', {
                      ns: 'forms',
                    }),
                  },
                ]}
              >
                <Select
                  disabled={!isSelectedCompany || employeeId}
                  allowClear
                  showSearch
                  mode="single"
                  className="w-max"
                  placeholder={t('selectEmployee', { ns: 'forms' })}
                  optionFilterProp="label"
                  loading={employeeOptionsIsLoading}
                  options={employeeOptions}
                />
              </Form.Item>
            </div>

            <Form.Item
              name="endDate"
              label={t('endOfWork', { ns: 'forms' })}
              rules={[
                {
                  required: true,
                  message: t('formValidation.selectDateRequired', {
                    ns: 'forms',
                  }),
                },
              ]}
            >
              <DatePicker
                disabled={!isSelectedCompany}
                className="w-full"
                format={DATE_FORMATS.DISPLAY}
                placeholder={t('endOfWork', { ns: 'forms' })}
              />
            </Form.Item>

            <Form.Item
              name="dateOfRequestCreate"
              label={t('dateOfRequestCreation', { ns: 'forms' })}
            >
              <DatePicker
                disabled={!isSelectedCompany}
                className="w-full"
                format={DATE_FORMATS.DISPLAY}
                placeholder={t('dateOfRequestCreation', { ns: 'forms' })}
              />
            </Form.Item>

            <Form.Item
              name="terminationReason"
              label={tt('reason')}
              rules={[{ required: true, message: tt('reasonRequired') }]}
            >
              <Select
                disabled={!isSelectedCompany}
                className="w-full"
                placeholder={tt('reasonPlaceholder')}
                options={reasonOptions}
              />
            </Form.Item>

            {reasonValue === 'OTHER' && (
              <Form.Item
                name="terminationReasonNote"
                label={tt('reasonNote')}
                rules={[{ required: true, message: tt('reasonNoteRequired') }]}
              >
                <Input
                  maxLength={TERMINATION_REASON_NOTE_MAX_LENGTH}
                  placeholder={tt('reasonNotePlaceholder')}
                />
              </Form.Item>
            )}

            <Form.Item
              name="terminationDocumentValue"
              label={tt('documentField')}
              extra={tt('documentFieldHint')}
              rules={[{ required: true, message: tt('documentRequired') }]}
            >
              <Select
                disabled={!isSelectedCompany}
                className="w-full"
                loading={templatesLoading}
                showSearch
                optionFilterProp="label"
                options={documentOptions}
              />
            </Form.Item>

            <hr className="my-5" />

            <ToggleHeader title={tt('attachExternal')} className="mb-3">
              <ToggleSwitch name="terminationDocument" form={form} />
            </ToggleHeader>

            {attachExternal && (
              <UploadFiles
                uploadName="terminateDocuments"
                fileList={uploadedFiles}
                onUploaded={handleUploadedFiles}
              />
            )}

            <AlertBox variant="info" className="mt-4 p-2 text-[12px]">
              {tt('noEmailOnSave')}
            </AlertBox>

            <EquipmentToReturn
              employeeId={selectedEmployee}
              hint
              className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2"
            />
          </Form>
        </div>
      </Dialog>
      <TerminationNotifyDialog
        employeeId={notifyEmployeeId}
        open={Boolean(notifyEmployeeId)}
        onClose={() => setNotifyEmployeeId(null)}
      />
    </>
  );
}
